// Angular
import { Component, AfterViewInit, PLATFORM_ID, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { isPlatformBrowser, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
// Transloco
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
// Environment
import { environment } from '@environment';
// Utils
import { CommonService } from '@core/utils';
// Services
import { LayoutService } from '@app/views/pages/layout/services';

// Local keys
const COOKIES_LS_KEY = `${environment.orgId}-cookie-consent-given`;
const COOKIE_SETTINGS = `${environment.orgId}-cookie-settings`;
const COOKIE_CONSENT_DATE_KEY = `${environment.orgId}-cookie-consent-date`;

// Registry import
import { PolicyRegistry, InventoryRow } from './cookie-popup.models';
import { REGISTRY } from './cookie-policy-registry';

@Component({
  selector: 'app-cookie-popup',
  templateUrl: './cookie-popup.component.html',
  styleUrls: ['./cookie-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TranslocoDirective, FormsModule, DatePipe],
})
export class CookiePopupComponent implements AfterViewInit {
  // Private dependencies
  private readonly translocoService = inject(TranslocoService);
  private readonly cs = inject(CommonService);
  private readonly platformId = inject<Object>(PLATFORM_ID);
  private readonly layoutService = inject(LayoutService);

  // State
  hasMadeChoice = true;
  isCustomizing = false;
  siteAnalyticsCookie = false;
  googleAdsCookie = false;
  language!: string;
  lastConsentDate: Date | null = null;
  registry: PolicyRegistry = REGISTRY;
  inventory: InventoryRow[] = [];
  customerType = signal<string>('1');

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.customerType.set(this.layoutService.getCustomerType());
    this.language = this.translocoService.getActiveLang();
    const consentGiven = this.cs.getFromLocalStorage(COOKIES_LS_KEY);

    if (consentGiven) {
      this.hasMadeChoice = true;
      this.getCookieSettings();
      this.updateGoogleCookieConsent();
    } else {
      this.hasMadeChoice = false;
    }

    // Build inventory from registry only
    this.cookieStorageInventoy();
  }

  // UI toggles
  showBanner(): void {
    this.hasMadeChoice = false;
    this.isCustomizing = false;
  }
  showCustomization(): void {
    this.isCustomizing = true;
  }
  hideCustomization(): void {
    this.isCustomizing = false;
  }

  onDeny(): void {
    this.siteAnalyticsCookie = false;
    this.googleAdsCookie = false;
    this.saveAndApplySettings();
  }

  onAllowAll(): void {
    this.siteAnalyticsCookie = true;
    this.googleAdsCookie = true;
    this.saveAndApplySettings();
  }

  onSavePreferences(): void {
    this.saveAndApplySettings();
  }

  private saveAndApplySettings(): void {
    this.hasMadeChoice = true;
    this.isCustomizing = false;
    const consentDate = new Date();
    this.lastConsentDate = consentDate;
    if (this.customerType()) this.layoutService.setCustomerType(this.customerType());

    // Save settings to local storage
    this.cs.setIntoLocalStorage(COOKIES_LS_KEY, 'true');
    this.cs.setIntoLocalStorage(COOKIE_SETTINGS, {
      analytics: this.siteAnalyticsCookie,
      ads: this.googleAdsCookie,
    });
    this.cs.setIntoLocalStorage(COOKIE_CONSENT_DATE_KEY, consentDate);

    // Send the update to Google
    this.updateGoogleCookieConsent();
  }

  private updateGoogleCookieConsent(): void {
    if (isPlatformBrowser(this.platformId) && typeof (window as any).gtag === 'function') {
      (window as any).gtag('consent', 'update', {
        ad_storage: this.googleAdsCookie ? 'granted' : 'denied',
        ad_user_data: this.googleAdsCookie ? 'granted' : 'denied',
        ad_personalization: this.googleAdsCookie ? 'granted' : 'denied',
        analytics_storage: this.siteAnalyticsCookie ? 'granted' : 'denied',
      });
    }
  }

  private getCookieSettings(): void {
    const cookieSettings = this.cs.getFromLocalStorage(COOKIE_SETTINGS);
    if (cookieSettings) {
      this.siteAnalyticsCookie = cookieSettings.analytics ?? false;
      this.googleAdsCookie = cookieSettings.ads ?? false;
    }
    const savedDateString = this.cs.getFromLocalStorage(COOKIE_CONSENT_DATE_KEY);
    if (savedDateString) this.lastConsentDate = new Date(savedDateString);
  }

  /**
   * Build the inventory table purely from the policy registry.
   */
  cookieStorageInventoy(): void {
    const rows: InventoryRow[] = this.registry.items.map((item) => ({
      ...item,
      vendorName: this.registry.vendors.find(v => v.id === item.vendorId)?.name ?? item.vendorId,
    }));
    this.inventory = rows;
  }
}
